import React from "react";

import { Button, Card, FormGroup, Intent, Slider } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";
import {
  CalculatorAction,
  CalculatorReducerState,
} from "@/components/pages_public/sales-comp/utils/types";
import { LIGHT_BACKGROUND, MIDTEXT_COLOR } from "@/css/constants";

import ValidatedNumericInput from "./ValidatedNumericInput";

interface CalculatorInputsProps {
  state: CalculatorReducerState;
  dispatch: React.Dispatch<CalculatorAction>;
}

function CalculatorInputs({ state, dispatch }: CalculatorInputsProps) {
  // Destructure state for easier access
  const {
    baseSalary,
    targetCommission,
    commissionRate,
    rampMonths1to3,
    rampMonths4to6,
    rampMonths7to9,
    rampMonths10to12,
    guaranteeMonths1to3,
    guaranteeMonths4to6,
    accelerators,
  } = state;
  return (
    <Card
      elevation={0}
      style={{
        width: "100%",
        backgroundColor: LIGHT_BACKGROUND,
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        padding: "24px",
      }}
    >
      {/* Base Compensation Section */}
      <Box>
        <Box
          as="h2"
          mb={3}
          fontSize="18px"
          fontWeight={600}
          color={MIDTEXT_COLOR}
        >
          Base Compensation
        </Box>
        <Box>
          <FormGroup
            label="Base Salary"
            labelFor="base-salary-input"
            style={{ marginBottom: 0 }}
          >
            <ValidatedNumericInput
              leftIcon="dollar"
              value={baseSalary}
              onValueChange={(value) => {
                dispatch({
                  type: "SET_BASE_SALARY",
                  payload: value,
                });
              }}
              maxDecimals={0}
              stepSize={1}
              fill
            />
          </FormGroup>
        </Box>
        <Box mt={3}>
          <FormGroup
            label="Target Commission"
            labelFor="target-commission-input"
            style={{ marginBottom: 0 }}
          >
            <ValidatedNumericInput
              leftIcon="dollar"
              value={targetCommission}
              onValueChange={(value) => {
                dispatch({
                  type: "SET_TARGET_COMMISSION",
                  payload: value,
                });
              }}
              maxDecimals={0}
              stepSize={1}
              fill
            />
          </FormGroup>
        </Box>
      </Box>

      {/* Commission Settings Section */}
      <Box className="bts" pt={3}>
        <Box
          as="h2"
          mb={3}
          fontSize="18px"
          fontWeight={600}
          color={MIDTEXT_COLOR}
        >
          Commission Settings
        </Box>
        <FormGroup
          label="Commission Rate (%)"
          labelFor="commission-rate-input"
          style={{ marginBottom: 0 }}
        >
          <ValidatedNumericInput
            value={commissionRate}
            onValueChange={(value) => {
              dispatch({
                type: "SET_COMMISSION_RATE",
                payload: value,
              });
            }}
            min={0}
            max={100}
            stepSize={0.1}
            fill
            maxDecimals={1}
            selectAllOnFocus
          />
        </FormGroup>
      </Box>

      {/* Ramp & Guarantees Section */}
      <Box className="bts" pt={4}>
        <Box
          as="h2"
          mb={4}
          fontSize="18px"
          fontWeight={600}
          color={MIDTEXT_COLOR}
        >
          Ramp & Guarantee
        </Box>
        <Box
          as="h3"
          mb={3}
          fontSize="16px"
          fontWeight={500}
          color={MIDTEXT_COLOR}
        >
          Quota Ramp (% of full quota)
        </Box>
        <Box>
          <FormGroup
            label="Months 1-3"
            labelFor="ramp-months-1-3"
            style={{ marginBottom: 4 }}
          >
            <Flex alignItems="center" css={{ gap: "20px" }}>
              <Slider
                min={0}
                max={100}
                stepSize={5}
                labelRenderer={false}
                value={rampMonths1to3}
                onChange={(value) =>
                  dispatch({ type: "SET_RAMP_MONTHS_1_TO_3", payload: value })
                }
              />
              <ValidatedNumericInput
                value={rampMonths1to3}
                onValueChange={(value) => {
                  dispatch({
                    type: "SET_RAMP_MONTHS_1_TO_3",
                    payload: value,
                  });
                }}
                min={0}
                max={100}
                maxDecimals={0}
                stepSize={1}
                style={{ width: "70px" }}
              />
            </Flex>
          </FormGroup>
        </Box>
        <Box>
          <FormGroup
            label="Months 4-6"
            labelFor="ramp-months-4-6"
            style={{ marginBottom: 4 }}
          >
            <Flex alignItems="center" css={{ gap: "20px" }}>
              <Slider
                min={0}
                max={100}
                stepSize={5}
                labelRenderer={false}
                value={rampMonths4to6}
                onChange={(value) =>
                  dispatch({ type: "SET_RAMP_MONTHS_4_TO_6", payload: value })
                }
              />
              <ValidatedNumericInput
                value={rampMonths4to6}
                onValueChange={(value) => {
                  dispatch({
                    type: "SET_RAMP_MONTHS_4_TO_6",
                    payload: value,
                  });
                }}
                min={0}
                max={100}
                maxDecimals={0}
                stepSize={1}
                style={{ width: "70px" }}
              />
            </Flex>
          </FormGroup>
        </Box>
        <Box>
          <FormGroup
            label="Months 7-9"
            labelFor="ramp-months-7-9"
            style={{ marginBottom: 4 }}
          >
            <Flex alignItems="center" css={{ gap: "20px" }}>
              <Slider
                min={0}
                max={100}
                stepSize={5}
                labelRenderer={false}
                value={rampMonths7to9}
                onChange={(value) =>
                  dispatch({ type: "SET_RAMP_MONTHS_7_TO_9", payload: value })
                }
              />
              <ValidatedNumericInput
                value={rampMonths7to9}
                onValueChange={(value) => {
                  dispatch({
                    type: "SET_RAMP_MONTHS_7_TO_9",
                    payload: value,
                  });
                }}
                min={0}
                max={100}
                maxDecimals={0}
                stepSize={1}
                style={{ width: "70px" }}
              />
            </Flex>
          </FormGroup>
        </Box>
        <Box>
          <FormGroup
            label="Months 10-12"
            labelFor="ramp-months-10-12"
            style={{ marginBottom: 4 }}
          >
            <Flex alignItems="center" css={{ gap: "20px" }}>
              <Slider
                min={0}
                max={100}
                stepSize={5}
                labelRenderer={false}
                value={rampMonths10to12}
                onChange={(value) =>
                  dispatch({ type: "SET_RAMP_MONTHS_10_TO_12", payload: value })
                }
              />
              <ValidatedNumericInput
                value={rampMonths10to12}
                onValueChange={(value) => {
                  dispatch({
                    type: "SET_RAMP_MONTHS_10_TO_12",
                    payload: value,
                  });
                }}
                min={0}
                max={100}
                maxDecimals={0}
                stepSize={1}
                style={{ width: "70px" }}
              />
            </Flex>
          </FormGroup>
        </Box>

        <Box mt={36}>
          <Box
            as="h3"
            mb={3}
            fontSize="16px"
            fontWeight={500}
            color={MIDTEXT_COLOR}
          >
            Guarantee (% of target commission)
          </Box>
          <Box>
            <FormGroup
              label="Months 1-3"
              labelFor="guarantee-months-1-3"
              style={{ marginBottom: 4 }}
            >
              <Flex alignItems="center" css={{ gap: "20px" }}>
                <Slider
                  min={0}
                  max={100}
                  stepSize={5}
                  labelRenderer={false}
                  value={guaranteeMonths1to3}
                  onChange={(value) =>
                    dispatch({
                      type: "SET_GUARANTEE_MONTHS_1_TO_3",
                      payload: value,
                    })
                  }
                />
                <ValidatedNumericInput
                  value={guaranteeMonths1to3}
                  onValueChange={(value) => {
                    dispatch({
                      type: "SET_GUARANTEE_MONTHS_1_TO_3",
                      payload: value,
                    });
                  }}
                  min={0}
                  max={100}
                  maxDecimals={0}
                  stepSize={1}
                  style={{ width: "70px" }}
                />
              </Flex>
            </FormGroup>
          </Box>
          <Box>
            <FormGroup
              label="Months 4-6"
              labelFor="guarantee-months-4-6"
              style={{ marginBottom: 4 }}
            >
              <Flex alignItems="center" css={{ gap: "20px" }}>
                <Slider
                  min={0}
                  max={100}
                  stepSize={5}
                  labelRenderer={false}
                  value={guaranteeMonths4to6}
                  onChange={(value) =>
                    dispatch({
                      type: "SET_GUARANTEE_MONTHS_4_TO_6",
                      payload: value,
                    })
                  }
                />
                <ValidatedNumericInput
                  value={guaranteeMonths4to6}
                  onValueChange={(value) => {
                    dispatch({
                      type: "SET_GUARANTEE_MONTHS_4_TO_6",
                      payload: value,
                    });
                  }}
                  min={0}
                  max={100}
                  maxDecimals={0}
                  stepSize={1}
                  style={{ width: "70px" }}
                />
              </Flex>
            </FormGroup>
          </Box>
        </Box>
      </Box>

      {/* Accelerator Settings Section */}
      <Box className="bts" pt={24}>
        <Flex alignItems="center" justifyContent="space-between" mb={2}>
          <Box
            as="h2"
            fontSize="18px"
            fontWeight={600}
            color={MIDTEXT_COLOR}
            margin={0}
          >
            Accelerator Settings{" "}
            {accelerators.length > 0 && `(${accelerators.length}/5)`}
          </Box>
        </Flex>

        <Box mb={3} fontSize="14px" color={MIDTEXT_COLOR}>
          Add up to 5 accelerator tiers to your compensation plan. Each tier
          represents a performance level with increased commission rates.
        </Box>

        {/* Show a message and add button when there are no accelerators */}
        {accelerators.length === 0 ? (
          <Box
            mb={4}
            p={4}
            style={{
              border: "1px dashed #CCC",
              borderRadius: "8px",
              backgroundColor: "#FAFCFF",
              textAlign: "center",
            }}
          >
            <Box fontSize="14px" mb={3} color={MIDTEXT_COLOR}>
              No accelerators configured. Commission rate will be flat at{" "}
              {commissionRate}% regardless of performance.
            </Box>
            <Button
              intent={Intent.PRIMARY}
              onClick={() => {
                dispatch({
                  type: "ADD_ACCELERATOR",
                  payload: {
                    threshold: 100,
                    multiplier: 1.5,
                  },
                });
              }}
            >
              Add First Accelerator
            </Button>
          </Box>
        ) : (
          /* Render each accelerator tier */
          accelerators.map((accelerator, index) => (
            <Box
              key={index}
              mb={2}
              p={3}
              style={{
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                backgroundColor: "#FAFAFA",
              }}
            >
              <Flex alignItems="center" justifyContent="space-between" mb={2}>
                <Box fontWeight={600} fontSize={14} color={MIDTEXT_COLOR}>
                  Tier {index + 1}
                </Box>

                {/* Always show Remove button - we can have zero accelerators */}
                <Button
                  small
                  minimal
                  intent={Intent.DANGER}
                  style={{ fontSize: 14 }}
                  onClick={() =>
                    dispatch({ type: "REMOVE_ACCELERATOR", payload: index })
                  }
                >
                  Remove
                </Button>
              </Flex>

              <Box width="100%">
                <FormGroup
                  label="Threshold (% of Quota)"
                  labelFor={`accelerator-threshold-${index}`}
                  style={{ marginBottom: 0 }}
                >
                  <ValidatedNumericInput
                    value={accelerator.threshold}
                    onValueChange={(value) => {
                      dispatch({
                        type: "UPDATE_ACCELERATOR",
                        payload: {
                          index,
                          accelerator: { threshold: value || 0 },
                        },
                      });
                    }}
                    onBlur={(e) =>
                      dispatch({
                        type: "UPDATE_ACCELERATOR",
                        payload: {
                          index,
                          accelerator: {
                            threshold: Number(e.target.value),
                          },
                          shouldSort: true, // Sort only on blur
                        },
                      })
                    }
                    min={0}
                    max={400}
                    maxDecimals={0}
                    stepSize={1}
                    fill
                  />
                </FormGroup>
              </Box>
              <Box width="100%" mt={3}>
                <FormGroup
                  label="Multiplier"
                  labelFor={`accelerator-multiplier-${index}`}
                  style={{ marginBottom: 0 }}
                >
                  <ValidatedNumericInput
                    value={accelerator.multiplier}
                    onValueChange={(value) => {
                      dispatch({
                        type: "UPDATE_ACCELERATOR",
                        payload: {
                          index,
                          accelerator: { multiplier: value || 0 },
                        },
                      });
                    }}
                    onBlur={(e) =>
                      dispatch({
                        type: "UPDATE_ACCELERATOR",
                        payload: {
                          index,
                          accelerator: {
                            multiplier: Number(e.target.value),
                          },
                          shouldSort: true, // Sort only on blur
                        },
                      })
                    }
                    min={1}
                    max={400}
                    maxDecimals={1}
                    stepSize={0.1}
                    fill
                  />
                </FormGroup>
              </Box>
            </Box>
          ))
        )}
        {/* Only show the add button if we have at least one accelerator already and fewer than 5 */}
        {accelerators.length > 0 && accelerators.length < 5 && (
          <Box>
            <Button
              intent={Intent.PRIMARY}
              onClick={() => {
                dispatch({
                  type: "ADD_ACCELERATOR",
                  payload: {
                    // Set the threshold to be higher than the last one if possible
                    threshold: Math.min(
                      400,
                      accelerators[accelerators.length - 1].threshold + 50,
                    ),
                    multiplier: 1.5,
                  },
                });
              }}
            >
              Add Another Accelerator
            </Button>
          </Box>
        )}
      </Box>
    </Card>
  );
}

export default CalculatorInputs;
